// src/components/AIMito/intentConfig.ts
import { store } from 'app/redux/store';
import { projectsApi } from 'app/services/projects';
import { userApi } from 'app/services/user';
import { TaxExtensionApi, ITaxExtension } from 'app/services/taxExtensions';
import { IProject } from 'app/services/responseTypes';
import { IClientBusiness } from 'app/services/responseTypes';
import { IUser } from 'app/services/user';
import { formatTeam } from './formatters';
import { AccountantApi } from 'app/services/accountant';
import { fetchEvents, formatEvents } from './intentHelpers';
import { ServicesApi } from 'app/services/payments/services';
import { Service } from 'app/services/responseTypes';
// ⚡ ADD once at the top

// one *really* single line – no \n anywhere in the string
const TICKET_BTN =
  '<button type="button" class="reset-button" ' +
  'onclick="document.querySelector(`#client-support-btn`)?.click()">' +
  'Open a ticket</button>';

// relative so the router in your app handles it
const NEW_Q_BTN =
  '<button type="button" class="reset-button" ' +
  "onclick=\"window.open('/dashboard/questionnaires','_self')\">" +
  'Submit&nbsp;a&nbsp;new&nbsp;questionnaire</button>';

const NEW_EXT_BTN =
  '<button type="button" class="reset-button" ' +
  "onclick=\"window.open('/dashboard/taxExtensions','_self')\">" +
  'Submit&nbsp;a&nbsp;new&nbsp;tax&nbsp;extension</button>';

const SERVICES_BTN =
  '<button type="button" class="reset-button" ' +
  "onclick=\"window.open('/dashboard/services/add-service','_self')\">" +
  'Explore&nbsp;Services</button>';

const CATEGORIZER_BTN =
  '<button type="button" class="reset-button open-categorizer">' +
  'Open Batch Categorizer</button>';

export interface SourceSpec {
  fetch: (prompt: string) => Promise<any[]>; // ⇢ always returns array
  format: (data: any[], wantCount: boolean, prompt: string) => string;
}
async function getCurrentUser() {
  const { data } = await store.dispatch(
    userApi.endpoints.getMyProfile.initiate(undefined, { forceRefetch: false }),
  );
  // `data` is undefined only if the token is bad ➜ let the caller handle
  return data?.user; // { id, roles, … }
}
export const INTENT_SOURCES: Record<string, SourceSpec> = {
  /* ───────────────────────── TAX RETURNS / QUESTIONNAIRES ───────────────────────── */
  TAX_RETURNS: {
    async fetch(_prompt: string) {
      const me = await getCurrentUser(); // may be undefined
      const isAccountant = me?.roles?.some(
        (r) => r.name?.toLowerCase() === 'accountant',
      );

      // only add the filter if we actually have an ID
      const filters = isAccountant && me?.id ? { accountant_id: me.id } : {};

      const { data } = await store.dispatch(
        projectsApi.endpoints.getProjects.initiate({
          per_page: 100,
          filters,
        }),
      );

      return (data as { data: IProject[] }).data;
    },
    format(arr: IProject[], wantCount, _prompt: string) {
      // ✅ business if at least one linked business
      const business = arr.filter((p) => p.businesses.length > 0).length;
      const individual = arr.length - business;

      if (wantCount) {
        return `You have ${arr.length} questionnaires (${business} Business • ${individual} Individual).`;
      }

      return (
        '👉 ' +
        NEW_Q_BTN + // ⚑ only the link
        '\n\n' +
        `Total Existing: ${arr.length} (Business: ${business}, Individual: ${individual})\n` +
        arr
          .map(
            (p) =>
              `• ${p.title || `${p.year} Tax Return`} – status: ${p.status}`,
          )
          .join('\n') +
        '\n\nIf you need any help, reach out support directly through tickets. ' +
        TICKET_BTN
      );
    },
  },

  /* ───────────────────────── TAX EXTENSIONS ───────────────────────── */
  TAX_EXTENSIONS: {
    async fetch(_prompt: string) {
      const { data } = await store.dispatch(
        TaxExtensionApi.endpoints.getTaxExtensions.initiate({
          per_page: 100,
          filters: {},
        }),
      );
      return (data as { data: ITaxExtension[] }).data;
    },

    format(arr: ITaxExtension[], wantCount, _prompt: string) {
      const individual = arr.filter(
        (e) => e.type.toLowerCase() === 'individual',
      ).length;
      const business = arr.length - individual;

      if (wantCount) {
        return `You have ${arr.length} tax-extension requests (${business} Business • ${individual} Individual).`;
      }

      return (
        '👉 ' +
        NEW_EXT_BTN + // ⚑ only the link
        '\n\n' +
        `Total Existing: ${arr.length} (Business: ${business}, Individual: ${individual})\n` +
        arr
          .map(
            (e) =>
              `• ${e.year} ${e.type} – status: ${e.status} – payment: ${e.payment_status}`,
          )
          .join('\n') +
        '\n\nIf you need any help, reach out support directly through tickets. ' +
        TICKET_BTN
      );
    },
  },

  /* ───────────────────────── PROJECTS (generic) ───────────────────────── */
  PROJECTS: {
    async fetch(_prompt: string) {
      const me = await getCurrentUser(); // may be undefined
      const isAccountant = me?.roles?.some(
        (r) => r.name?.toLowerCase() === 'accountant',
      );

      // only add the filter if we actually have an ID
      const filters = isAccountant && me?.id ? { accountant_id: me.id } : {};
      const { data } = await store.dispatch(
        projectsApi.endpoints.getProjects.initiate({
          per_page: 100,
          filters,
        }),
      );
      return (data as { data: IProject[] }).data;
    },

    /* ───  PROJECTS (generic)  ─── */
    format(arr: IProject[], wantCount, _prompt: string) {
      const business = arr.filter((p) => p.businesses.length > 0).length;
      const individual = arr.length - business;

      if (wantCount) {
        return `You have ${arr.length} projects (${business} Business • ${individual} Individual).`;
      }

      return (
        `Total Existing: ${arr.length} (Business: ${business}, Individual: ${individual})\n` +
        arr.map((p) => `• ${p.title} – ${p.status}`).join('\n') +
        '\n\nIf you need any help, reach out support directly through tickets. ' +
        TICKET_BTN
      );
    },
  },

  /* ───────────────────────── BUSINESSES ───────────────────────── */
  BUSINESSES: {
    async fetch(_prompt: string) {
      const { data } = await store.dispatch(
        userApi.endpoints.getUserBusinesses.initiate(),
      );
      return data as IClientBusiness[];
    },

    format(arr: IClientBusiness[], wantCount, _prompt: string) {
      if (wantCount) return `You have ${arr.length} businesses.`;

      return (
        `Total Existing: ${arr.length}\n` +
        arr.map((b) => `• ${b.name}`).join('\n') +
        '\n\nIf you need any help, reach out support directly through tickets. ' +
        TICKET_BTN
      );
    },
  },

  /* ───────────────────────── ACCOUNTANTS / TEAM ───────────────────────── */
  ACCOUNTANTS: {
    async fetch(_prompt: string) {
      // 1) get the basic team array (same as before)
      const { data } = await store.dispatch(
        userApi.endpoints.getMyProfile.initiate(),
      );

      const team = (data as any).user.team_members as IUser[];

      // 2) lazily patch rows that don’t have schedule_platform_link
      const patched = await Promise.all(
        team.map(async (u) => {
          // already has a link → nothing to do
          if (u.accountant_profile?.schedule_platform_link) return u;

          // fetch the full accountant profile once for this user
          const { data: prof } = await store.dispatch(
            AccountantApi.endpoints.getAccountantProfiles.initiate(u.id),
          );

          const link = prof?.accountant_profile.schedule_platform_link ?? '';

          // merge the link back into the array element
          return {
            ...u,
            accountant_profile: {
              ...(u.accountant_profile ?? {}),
              schedule_platform_link: link,
            },
          };
        }),
      );

      return patched;
    },

    format(arr: IUser[], wantCount, _prompt: string) {
      if (wantCount) return `You have ${arr.length} assigned accountants.`;

      return (
        `Total Existing: ${arr.length}\n` +
        formatTeam(arr) +
        '\n\nIf you need any help, reach out support directly through tickets. ' +
        TICKET_BTN
      );
    },
  },

  /* ───────────────────────── SERVICES ───────────────────────── */
  SERVICES: {
    async fetch(prompt: string) {
      const { data } = await store.dispatch(
        ServicesApi.endpoints.getServices.initiate(),
      );
      const services = (data as Service[]) || [];
      const words = prompt
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length > 2);
      const scored = services.map((s) => {
        const text = `${s.title} ${s.description}`.toLowerCase();
        const score = words.reduce(
          (acc, w) => acc + (text.includes(w) ? 1 : 0),
          0,
        );
        return { s, score };
      });
      scored.sort((a, b) => b.score - a.score);
      const top = scored.filter((r) => r.score > 0).map((r) => r.s);
      const result = top.length > 0 ? top.slice(0, 10) : services.slice(0, 10);
      return result;
    },

    format(arr: Service[], _wantCount: boolean, _prompt: string) {
      return (
        '👉 ' +
        SERVICES_BTN +
        '\n\n' +
        arr
          .map(
            (s) =>
              `• <a href="#" class="service-link" data-service-id="${s.id}">${
                s.title
              } – $${s.prices[0]?.amount_cents / 100}</a>`,
          )
          .join('\n')
      );
    },
  },
  /* ───────────────────────── BATCH CATEGORIZATION ───────────────────────── */
  BATCH_CATEGORIZATION: {
    async fetch(_prompt: string) {
      return [];
    },
    format(_arr: any[], _wantCount: boolean, _prompt: string) {
      return '👉 ' + CATEGORIZER_BTN;
    },
  },
  /* ───────────────────────── UPCOMING DATES ───────────────────────── */
  UPCOMING_DATES: {
    fetch: (_p: string) => fetchEvents(), // Promise<[...]> ✓
    format: (d, wantCount, _p: string) => formatEvents(d as any, wantCount),
  },
  ANNOUNCEMENTS: {
    fetch: (_p: string) => fetchEvents(),
    format: (d, wantCount, _p: string) => formatEvents(d as any, wantCount),
  },
};
